import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchInsuredSession, hasAuthHint } from "@/modules/Auth/api/authApi.js";
import { setAuthNavigator } from "@/shared/services/httpClient.js";
// The speciality master is a session-wide singleton that every quote-flow
// page keys off of (questions tree, ILF/DLF). It used to be fetched only on
// the landing page, which broke any *direct* load of a later step (refresh /
// deep link / nav that skips the landing): specialityId stayed undefined and
// the page rendered blank. Fetch it on any path that consumes it so a direct
// load hydrates the same as forward navigation.
const SPECIALITY_MASTER_PATHS = new Set([
  "/", "/classification", "/register", "/license",
]);
// Refresh-survival paths — when the user reloads on one of these, we re-hydrate
// the active order from `flowSubmissionId` (restored from sessionStorage) instead
// of bouncing to /dashboard.
const ORDER_REHYDRATE_PATHS = new Set([
  "/license", "/coverage", "/underwriting",
  "/payment", "/reviewDocusign", "/binder-invoice", "/underwriter-review",
]);
// Submission-questions are only needed on the four wizard pages that render
// them. Pages like /payment, /complete-order, /binder-invoice, /order-details
// and /profile must NOT trigger the fetch.
const SUBMISSION_QUESTIONS_PATHS = new Set([
  "/license", "/coverage", "/underwriting", "/reviewDocusign",
]);
import { fetchSpecialityByCode } from "@/modules/Quote/api/specialityApi.js";
import {
  fetchSubmissionQuestions,
  mergeAnswerMaps,
  recomputeHiddenQuestionIds,
  resolveVisibilityTargets,
} from "@/modules/Quote/api/questionsApi.js";
import { isInsuredSessionPayload } from "@/modules/Quote/utils/submission.js";
import { pathToStepIndex, STEP_PATHS } from "@/modules/Quote/constants.js";
import { DEFAULT_SPECIALITY_CODE } from "@/shared/services/config.js";
import { refreshPaymentOrder } from "@/modules/Payment/services/paymentOrderService.js";

import { useStore } from "@/shared/store/useStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import { useSpeciality } from "@/hooks/useSpeciality.js";

/**
 * App-level bootstrap. Mount this once from <App/> — it doesn't render
 * anything, it just runs the cross-cutting effects that used to live in
 * the QuoteProvider:
 *
 *   1. Session restore on first boot — always call /insured/session.
 *   2. Speciality master fetch on the landing page.
 *   3. Speciality-based master questions fetch (anonymous pre-signup).
 *   4. Submission-scoped questions fetch (steps 2-6 with submission id).
 *   5. Reset transient quote answers when submission id changes.
 *   6. Clear sign-in error on /signin navigation.
 */
export function useAppBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionBootRef = useRef(false);
  const specialityMaster = useSpeciality();
  const flowSubmissionId = useStore(submissionStore, (s) => s.flowSubmissionId);
  const sessionReady = useStore(sessionStore, (s) => s.sessionReady);
  const dashView = useStore(sessionStore, (s) => s.dashView);
  const submissionQuestionsLoadedSidRef = useRef(null);

  // 0. Give httpClient's 401→sign-in redirect an SPA navigator so it stops
  //    tearing down the document with `window.location.replace` (which cancels
  //    every in-flight sibling request). Registered as early as possible so a
  //    boot-time 401 uses SPA navigation too.
  useEffect(() => {
    setAuthNavigator(navigate);
    return () => setAuthNavigator(null);
  }, [navigate]);

  // 1. Session restore on first boot. The HttpOnly access-token cookie is
  //    invisible to JS, so we just call /insured/session — the backend
  //    tells us whether we're authenticated. No localStorage marker.
  useEffect(() => {
    if (sessionBootRef.current) return undefined;
    sessionBootRef.current = true;
    // Skip the session call entirely when there's no auth hint cookie.
    // The HttpOnly access token is invisible to JS, but the hint cookie
    // (set on sign-in / cleared on logout) tells us whether to bother.
    if (!hasAuthHint()) {
      sessionStore.sessionReady = true;
      return undefined;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await fetchInsuredSession({ signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (isInsuredSessionPayload(data)) {
          insuredProfileStore.insuredProfile = data;
          const p = location.pathname;
          const stayHere =
            p === "/reviewDocusign" ||
            p === "/payment" ||
            p === "/binder-invoice" ||
            p === "/underwriter-review" ||
            p === "/order-details" ||
            p === "/profile" ||
            p === "/complete-order" ||
            p === "/dashboard";
          if (!stayHere) {
            sessionStore.dashView = "dashboard";
            navigate("/dashboard", { replace: true });
          }
        }
      } catch (e) {
        // axios aborts throw CanceledError; native aborts throw AbortError.
        if (e?.name === "AbortError" || e?.name === "CanceledError") return;
        // 401/403/404 → user just isn't signed in. No action needed.
      } finally {
        if (!ctrl.signal.aborted) sessionStore.sessionReady = true;
      }
    })();
    return () => { ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Speciality master fetch — on any quote-flow path that consumes it
  //    (`/` and the wizard steps that read specialityId), AND
  //    not being redirected to /dashboard. We re-check on every path change
  //    so that an authenticated user who clicks "Start a new quote" from
  //    /dashboard still gets the fetch, and a direct load / refresh of a
  //    later step hydrates the master instead of rendering blank.
  useEffect(() => {
    if (!sessionReady || specialityMaster) return undefined;
    if (!SPECIALITY_MASTER_PATHS.has(location.pathname)) return undefined;
    if (dashView !== null) return undefined;
    questionsStore.specialityLoadError = null;
    // No AbortController here. `fetchSpecialityByCode` is a per-tab cached
    // singleton that writes its result into specialityStore itself and dedupes
    // concurrent callers. Passing an abort signal let a dependency change (e.g.
    // `dashView` flipping) cancel the SHARED request and evict its cache
    // (specialityApi deletes the entry on the resulting CanceledError) — that
    // surfaced as a "(canceled)" speciality call plus a redundant refetch, and
    // could briefly strand `specialityMaster` as null. Fire it and only record
    // a genuine error; a superseded run is harmless because the cache dedupes.
    fetchSpecialityByCode(DEFAULT_SPECIALITY_CODE)
      .catch((e) => {
        if (e?.name === "AbortError" || e?.name === "CanceledError") return;
        questionsStore.specialityLoadError = e;
      });
    return undefined;
  }, [location.pathname, dashView, sessionReady, specialityMaster]);

  // 3. Speciality-based question fetch is owned by `useQuestionsAutoLoad`
  //    (mounted by ClassificationPage + LicenseScopePage). The hook
  //    short-circuits when the master tree is already cached for the
  //    requested speciality id, so a single network call covers steps
  //    2 + 4 of the anonymous flow.

  // 4. Landing (/) — wipe answers + visited groups so a new quote never
  // inherits a previous one's answers. This ran on /quote until that page
  // was removed; / is now the flow's entry point.
  useEffect(() => {
    if (location.pathname !== "/") return;
    questionsStore.questionAnswers = {};
    questionsStore.coverageAnswers = {};
    questionsStore.impactAnswers = {};
    questionsStore.underwritingAnswers = {};
    questionsStore.visitedQuestionGroups = [];
  }, [location.pathname]);

  // 5. Submission-scoped question fetch — only on the four wizard pages
  // that actually render the question tree (/license, /coverage,
  // /underwriting, /reviewDocusign). /payment, /complete-order,
  // /binder-invoice, /order-details, /profile must NOT trigger this fetch.
  // Fires once per (sid) and only when the cache is empty.
  //
  // Gated on `sessionReady` (like effects 2 and 9): on a reload of a wizard
  // page the persisted `flowSubmissionId` is valid immediately, so without
  // this gate the fetch races effect 1's `/insured/session` call. If that
  // session call 401s and the refresh also fails, httpClient's redirect used
  // to hard-navigate and cancel this still-in-flight request (the "(canceled)"
  // symptom). Waiting for `sessionReady` means the session outcome is known
  // before we start — and any redirect now happens via SPA navigation anyway.
  useEffect(() => {
    if (!sessionReady) return undefined;
    // D4 guard — reject `0` / non-positive / non-numeric ids before they
    // hit `/questions/0/questions`.
    const sidNum = Number(flowSubmissionId);
    if (!Number.isFinite(sidNum) || sidNum <= 0) return undefined;
    if (!SUBMISSION_QUESTIONS_PATHS.has(location.pathname)) return undefined;
    const cached = questionsStore.submissionQuestionGroups;
    // Cache is only valid when it belongs to the active submission — resuming
    // a different sid leaves the previous tree in the store, so a length check
    // alone would skip the refetch and show the wrong submission's answers.
    const hasCached = Array.isArray(cached) && cached.length > 0
      && questionsStore.submissionQuestionsSid === String(flowSubmissionId);
    if (hasCached) return undefined;
    const ctrl = new AbortController();
    // Derive the "master step" flag straight from the URL rather than the
    // `step` store value. `step` is synced from the path by effect 7 ONE
    // render later, so depending on it here made this effect re-run the
    // instant `step` caught up — aborting the just-fired `/questions` request
    // (the "(canceled)" symptom) and refetching. The path is the source of
    // truth and is already a dependency, so keying off it is both correct and
    // churn-free.
    const pathStep = pathToStepIndex(location.pathname);
    const isMasterStep = pathStep === 2 || pathStep === 4;
    questionsStore.submissionQuestionsLoading = true;
    questionsStore.submissionQuestionsError = null;
    if (isMasterStep) {
      questionsStore.questionsLoading = true;
      questionsStore.questionsError = null;
    }
    fetchSubmissionQuestions(flowSubmissionId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const groups = Array.isArray(data) ? data : [];
        if (isMasterStep && (!questionsStore.questionGroups || questionsStore.questionGroups.length === 0)) {
          questionsStore.questionGroups = groups; // unaugmented — unrelated master-tree fallback
        }
        // Visibility/Display-Order resolution (choke-point). Order matters:
        // augment first, store the augmented tree (every downstream consumer
        // reads options carrying visibilityHide/ShowTargetIds), seed the
        // show-target set, then compute + commit the hidden set via
        // setHiddenQuestionIds so FR4's answer-clearing runs on first load too.
        const { groups: augmentedGroups, showTargetQuestionIds } = resolveVisibilityTargets(groups);
        questionsStore.submissionQuestionGroups = augmentedGroups;
        questionsStore.submissionQuestionsSid = flowSubmissionId;
        submissionQuestionsLoadedSidRef.current = flowSubmissionId;
        questionsStore.showTargetQuestionIds = showTargetQuestionIds;
        const liveAnswers = mergeAnswerMaps(
          questionsStore.questionAnswers,
          questionsStore.coverageAnswers,
          questionsStore.underwritingAnswers,
        );
        questionsStore.setHiddenQuestionIds(
          recomputeHiddenQuestionIds(augmentedGroups, liveAnswers, showTargetQuestionIds),
        );
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.name === "AbortError") return;
        questionsStore.submissionQuestionsError = e;
        if (isMasterStep) questionsStore.questionsError = e;
      })
      .finally(() => {
        if (ctrl.signal.aborted) return;
        questionsStore.submissionQuestionsLoading = false;
        if (isMasterStep) questionsStore.questionsLoading = false;
      });
    return () => { ctrl.abort(); };
    // `step` is intentionally NOT a dependency — see the isMasterStep comment
    // above. It's derived from `location.pathname`, which is already listed.
  }, [location.pathname, flowSubmissionId, sessionReady]);

  // 6. Reset coverage / impact / underwriting answers on submission change.
  useEffect(() => {
    questionsStore.coverageAnswers = {};
    questionsStore.impactAnswers = {};
    questionsStore.underwritingAnswers = {};
    questionsStore.underwritingSaveError = null;
    // Drop the previous submission's Visibility state immediately (before
    // effect #5's fetch for the new sid resolves) so a stale hidden set can't
    // leak into the next submission's first render.
    questionsStore.setHiddenQuestionIds(new Set());
    questionsStore.showTargetQuestionIds = new Set();
  }, [flowSubmissionId]);

  // 7. Path → step sync. Keeps submissionStore.step in sync with URL when
  //    the user navigates via Back/Forward.
  useEffect(() => {
    const i = STEP_PATHS.indexOf(location.pathname);
    if (i !== -1 && submissionStore.step !== i) submissionStore.step = i;
  }, [location.pathname]);

  // 8. Rehydrate cached ILF/DLF inputs into form on first load.
  useEffect(() => {
    if (quoteFormStore.zip && quoteFormStore.effDate) return;
    const cached = ilfDlfStore.cacheParams;
    if (!cached) return;
    if (!quoteFormStore.zip && cached.zipcode) quoteFormStore.zip = cached.zipcode;
    if (!quoteFormStore.effDate && cached.effectiveDate) quoteFormStore.effDate = cached.effectiveDate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 9. Refresh-survival rehydrate — on /payment, /reviewDocusign,
  //    /binder-invoice, /underwriter-review the page expects an active
  //    paymentOrderDetails. `flowSubmissionId` is restored from
  //    sessionStorage by submissionStore; re-fetch the order so the page
  //    can render its summary instead of being kicked out by FlowLayout's
  //    guard (or ReviewDocusignPage's `!flowSubmissionId` redirect).
  useEffect(() => {
    if (!sessionReady) return;
    if (!ORDER_REHYDRATE_PATHS.has(location.pathname)) return;
    if (!flowSubmissionId) return;
    refreshPaymentOrder(flowSubmissionId, { ifMissing: true });
  }, [sessionReady, location.pathname, flowSubmissionId]);
}
